import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Flag } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable report button.
 * Props:
 *   targetType: 'user' | 'message' | 'request'
 *   targetId: string
 *   label?: string  (default: "舉報")
 *   size?: 'sm' | 'default'
 *   variant?: shadcn button variant
 *   testId?: string  (defaults to `report-btn-${targetId}`)
 */
export const ReportButton = ({ targetType, targetId, label = '舉報', size = 'sm', variant = 'ghost', testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('scam');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reports`,
        { target_type: targetType, target_id: targetId, reason, description: description || undefined },
        { withCredentials: true }
      );
      toast.success('舉報已送出，管理員會盡快處理');
      setOpen(false);
      setReason('scam');
      setDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || '舉報失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className="rounded-sm text-xs text-[#C93B2B] hover:bg-[#FEF8F0]"
        data-testid={testId || `report-btn-${targetId}`}
      >
        <Flag className="w-3 h-3 mr-1" strokeWidth={1.5} />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">舉報</DialogTitle>
            <DialogDescription>選擇舉報原因，管理員會審核處理</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>原因</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="rounded-sm mt-2" data-testid="report-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scam">詐騙</SelectItem>
                  <SelectItem value="fake">虛假資訊</SelectItem>
                  <SelectItem value="harassment">騷擾</SelectItem>
                  <SelectItem value="spam">垃圾訊息</SelectItem>
                  <SelectItem value="off_topic">答非所問</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>詳細說明（可選）</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-sm mt-2"
                placeholder="請提供更多細節，協助管理員判斷"
                rows={4}
                data-testid="report-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">取消</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm" data-testid="submit-report">
              {submitting ? '提交中...' : '送出舉報'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportButton;
